import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import ReactGA from "react-ga4";
import { useIntl } from "react-intl";
import React, { FC, ReactNode, useMemo } from "react";
import { Formik, FormikProps } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import {
  AppointmentCreatePaymentParams,
  AppointmentCreatePaymentSuccessResponse,
  GetCustomerCheckoutFormDataResponse,
  SaveAppointmentParams,
} from "@Shape-Digital/kudzu-data/lib/types/actions";
import { PatientFormValues } from "../AppointmentCheckoutPatientForm";
import { GuardianFormValues } from "../AppointmentCheckoutGuardianForm";
import { OtherPersonFormValues } from "../AppointmentCheckoutOtherPersonForm";

import getValidationSchema, {
  CHECKOUT_DATE_FORMAT,
} from "./getValidationSchema";
import useTranslations from "./useTranslations";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { useAuthContext } from "../../Auth/AuthContextProvider/index";
import {
  MAX_STRING_INPUT_LENGTH,
  MIN_STRING_INPUT_LENGTH,
} from "../../../common/constants";
import { clearStorageAppointment } from "../AppointmentBookingContext/storageAppointmentHelpers";
import getDisplayErrorMessage from "../../../common/getDisplayErrorMessage";

const parseZeroValue = (value: number | null | undefined): string | number => {
  if (value || value === 0) return value;
  return "";
};

export type AppointmentCustomerDetails = Pick<
  DatabaseRow<"appointment_customer_details">,
  | "id"
  | "customer_id"
  | "type"
  | "first_name"
  | "last_name"
  | "email"
  | "mobile_phone_number"
>;

export type AppointmentPatientDetails = Pick<
  DatabaseRow<"appointment_patient_details">,
  | "id"
  | "date_birth"
  | "height_in"
  | "height_ft"
  | "weight_lbs"
  | "first_name"
  | "last_name"
  | "email"
  | "mobile_phone_number"
>;

const getPatientInitialValues = ({
  customerFormData,
  selectedCustomer,
  customerDetails,
  patientDetails,
}: {
  customerFormData: GetCustomerCheckoutFormDataResponse | null;
  selectedCustomer?: DatabaseRow<"customers"> | null;
  customerDetails?: AppointmentCustomerDetails;
  patientDetails?: AppointmentPatientDetails;
}) => {
  if (selectedCustomer) {
    const initialValues: PatientFormValues = {
      pregnant: "no",
      wearingDevice: "",
      email: selectedCustomer.email,
      firstName: selectedCustomer.first_name,
      lastName: selectedCustomer.last_name,
      phoneNumber: selectedCustomer.mobile_phone_number || "",
      additionalPhoneNumber: selectedCustomer.additional_phone_number || "",
      dateOfBirth: selectedCustomer.date_birth
        ? format(parseISO(selectedCustomer.date_birth), CHECKOUT_DATE_FORMAT)
        : "",
      heightFt: "",
      heightIn: "",
      weightLbs: "",
    };

    return initialValues;
  }

  const dateOfBirth =
    patientDetails?.date_birth || customerFormData?.patient?.date_birth;

  const initialValues: PatientFormValues = {
    pregnant: "no",
    wearingDevice: "",
    email: customerDetails?.email || customerFormData?.customer.email || "",
    firstName:
      customerDetails?.first_name ||
      customerFormData?.customer.first_name ||
      "",
    lastName:
      customerDetails?.last_name || customerFormData?.customer.last_name || "",
    phoneNumber:
      customerDetails?.mobile_phone_number ||
      customerFormData?.customer.mobile_phone_number ||
      "",
    additionalPhoneNumber:
      customerFormData?.customer.additional_phone_number || "",
    dateOfBirth: dateOfBirth
      ? format(parseISO(dateOfBirth), CHECKOUT_DATE_FORMAT)
      : "",
    heightFt: parseZeroValue(patientDetails?.height_ft),
    heightIn: parseZeroValue(patientDetails?.height_in),
    weightLbs: parseZeroValue(patientDetails?.weight_lbs),
  };

  return initialValues;
};

const getGuardianInitialValues = ({
  customerFormData,
  selectedCustomer,
  customerDetails,
  patientDetails,
  pendingEmail,
}: {
  customerFormData: GetCustomerCheckoutFormDataResponse | null;
  selectedCustomer?: DatabaseRow<"customers"> | null;
  customerDetails?: AppointmentCustomerDetails;
  patientDetails?: AppointmentPatientDetails;
  pendingEmail?: string;
}) => {
  if (selectedCustomer) {
    const initialValues: GuardianFormValues = {
      guardianEmail: selectedCustomer?.email,
      guardianPhoneNumber: selectedCustomer.mobile_phone_number || "",
      guardianFirstName: selectedCustomer.first_name,
      guardianLastName: selectedCustomer.last_name,
      additionalPhoneNumber: selectedCustomer.additional_phone_number || "",
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      heightFt: "",
      heightIn: "",
      weightLbs: "",
      pregnant: "no",
      wearingDevice: "",
    };

    return initialValues;
  }

  const patientDateOfBirth = patientDetails?.date_birth;

  const initialValues: GuardianFormValues = {
    guardianEmail:
      customerDetails?.email ||
      customerFormData?.customer.email ||
      pendingEmail ||
      "",
    guardianFirstName:
      customerDetails?.first_name ||
      customerFormData?.customer.first_name ||
      "",
    guardianLastName:
      customerDetails?.last_name || customerFormData?.customer.last_name || "",
    guardianPhoneNumber:
      customerDetails?.mobile_phone_number ||
      customerFormData?.customer.mobile_phone_number ||
      "",
    selectedPatientId: undefined,
    additionalPhoneNumber:
      customerFormData?.customer.additional_phone_number || "",
    firstName: patientDetails?.first_name || "",
    lastName: patientDetails?.last_name || "",
    dateOfBirth: patientDateOfBirth
      ? format(parseISO(patientDateOfBirth), CHECKOUT_DATE_FORMAT)
      : "",
    heightFt: parseZeroValue(patientDetails?.height_ft),
    heightIn: parseZeroValue(patientDetails?.height_in),
    weightLbs: parseZeroValue(patientDetails?.weight_lbs),
    pregnant: "no",
    wearingDevice: "",
  };

  return initialValues;
};

const getOtherPersonInitialValues = ({
  selectedCustomer,
  customerDetails,
  patientDetails,
}: {
  selectedCustomer?: DatabaseRow<"customers"> | null;
  customerDetails?: AppointmentCustomerDetails;
  patientDetails?: AppointmentPatientDetails;
}) => {
  if (selectedCustomer) {
    const initialValues: OtherPersonFormValues = {
      otherPersonFirstName: "",
      otherPersonLastName: "",
      otherPersonEmail: "",
      firstName: selectedCustomer.first_name,
      lastName: selectedCustomer.last_name,
      email: selectedCustomer.email,
      phoneNumber: selectedCustomer.mobile_phone_number || "",
      dateOfBirth: selectedCustomer?.date_birth
        ? format(parseISO(selectedCustomer?.date_birth), CHECKOUT_DATE_FORMAT)
        : "",
      pregnant: "no",
      wearingDevice: "",
      heightFt: "",
      heightIn: "",
      weightLbs: "",
    };

    return initialValues;
  }

  const initialValues: OtherPersonFormValues = {
    otherPersonFirstName: customerDetails?.first_name || "",
    otherPersonLastName: customerDetails?.last_name || "",
    otherPersonEmail: customerDetails?.email || "",
    firstName: patientDetails?.first_name || "",
    lastName: patientDetails?.last_name || "",
    email: patientDetails?.email || "",
    phoneNumber: patientDetails?.mobile_phone_number || "",
    dateOfBirth: patientDetails?.date_birth
      ? format(parseISO(patientDetails?.date_birth), CHECKOUT_DATE_FORMAT)
      : "",
    pregnant: "no",
    wearingDevice: "",
    heightFt: parseZeroValue(patientDetails?.height_ft),
    heightIn: parseZeroValue(patientDetails?.height_in),
    weightLbs: parseZeroValue(patientDetails?.weight_lbs),
  };

  return initialValues;
};

const getInitialTouched = <T extends Record<string, string>>(
  values: Record<keyof T, string | number>,
): Record<keyof T, boolean> => {
  return Object.keys(values).reduce(
    (resultObj, key) => ({ ...resultObj, [key]: !!values[key] }),
    {} as Record<keyof T, boolean>,
  );
};

const getValidateOnMount = <T extends Record<string, string>>(
  values: Record<keyof T, string | number>,
): boolean => {
  return Object.keys(values).some((valueKey) => !!values[valueKey]);
};

type AppointmentCheckoutFormSectionWrapperProps = {
  personType:
    | "patient"
    | "legalGuardian"
    | "familyMember"
    | "otherPerson"
    | null;
  customerFormData: GetCustomerCheckoutFormDataResponse;
  selectedCustomer?: DatabaseRow<"customers"> | null;
  isPaymentRequired: boolean;
  customerDetails?: AppointmentCustomerDetails;
  patientDetails?: AppointmentPatientDetails;
  pendingEmail?: string;
  children?:
    | ((
        props: FormikProps<
          | PatientFormValues
          | GuardianFormValues
          | OtherPersonFormValues
          | Record<string, never>
        >,
      ) => ReactNode)
    | ReactNode;
};

const getPhoneNumber = (str: string): string => {
  return `+${str.replace(/\D/g, "")}`;
};

const getDateOfBirth = (str: string) =>
  formatISO(parse(str, CHECKOUT_DATE_FORMAT, Date.now()), {
    representation: "date",
  });

const getPaymentUrl = (
  createdPaymentData: AppointmentCreatePaymentSuccessResponse,
  appointmentId: string,
  defaultErrorText: string,
): string => {
  const { providerType } = createdPaymentData;

  switch (providerType) {
    case "stripe": {
      const { clientSecret } = createdPaymentData;

      return `/${appointmentId}/payment/stripe?clientSecret=${clientSecret}`;
    }
    default: {
      throw new Error(defaultErrorText);
    }
  }
};

const AppointmentCheckoutFormSectionWrapper: FC<
  AppointmentCheckoutFormSectionWrapperProps
> = ({
  personType,
  children,
  customerFormData,
  selectedCustomer,
  isPaymentRequired,
  customerDetails,
  patientDetails,
  pendingEmail,
}) => {
  const { translations, validationSchemaTranslations } = useTranslations({
    minimumValue: MIN_STRING_INPUT_LENGTH,
    maximumValue: MAX_STRING_INPUT_LENGTH,
  });
  const { formatMessage } = useIntl();

  const appointmentCreatePayment = useFirebaseAppFunction(
    "appointmentCreatePayment",
  );

  const saveAppointment = useFirebaseAppFunction("saveAppointment");

  const { awsSession } = useAuthContext();
  const { appointmentId } = useParams();

  const navigate = useNavigate();

  const patientInitialValues = useMemo(
    () =>
      getPatientInitialValues({
        customerFormData,
        selectedCustomer,
        customerDetails,
        patientDetails,
      }),
    [customerFormData, selectedCustomer, customerDetails, patientDetails],
  );

  const guardianInitialValues = useMemo(
    () =>
      getGuardianInitialValues({
        customerFormData,
        selectedCustomer,
        customerDetails,
        patientDetails,
        pendingEmail,
      }),
    [
      customerFormData,
      selectedCustomer,
      customerDetails,
      patientDetails,
      pendingEmail,
    ],
  );

  const otherPersonInitialValues = useMemo(
    () =>
      getOtherPersonInitialValues({
        selectedCustomer,
        customerDetails,
        patientDetails,
      }),
    [selectedCustomer, customerDetails, patientDetails],
  );

  switch (personType) {
    case "patient": {
      const isLoggedInPatient =
        !!customerFormData?.customer?.email || !!selectedCustomer;

      if (isLoggedInPatient) {
        const onSubmit = async (values: GuardianFormValues) => {
          try {
            if (!appointmentId) {
              throw new Error(translations.defaultError);
            }

            const customerId =
              selectedCustomer?.id || awsSession?.getIdToken()?.payload?.sub;

            const params:
              | AppointmentCreatePaymentParams
              | SaveAppointmentParams = {
              appointmentId,
              customerId,
              creatorType: "patient",
              email: values.guardianEmail,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: getPhoneNumber(values.guardianPhoneNumber),
              additionalPhoneNumber: values.additionalPhoneNumber
                ? getPhoneNumber(values.additionalPhoneNumber)
                : undefined,
              dateOfBirth: getDateOfBirth(values.dateOfBirth),
              selectedPatientId: values.selectedPatientId,
              pregnant: values.pregnant,
              wearingDevice: values.wearingDevice,
              heightFt: +values.heightFt,
              heightIn: +values.heightIn,
              weightLbs: +values.weightLbs,
            };

            if (isPaymentRequired) {
              ReactGA.event("Click confirm and pay", {
                patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
              });

              const { data: appointmentCreatePaymentResponse } =
                await appointmentCreatePayment(params);

              if (appointmentCreatePaymentResponse.status === "error") {
                throw new Error(translations.errorEmailInUse);
              }

              const paymentUrl = getPaymentUrl(
                appointmentCreatePaymentResponse.data,
                appointmentId,
                translations.defaultError,
              );

              clearStorageAppointment();
              navigate(paymentUrl);
            }

            if (!isPaymentRequired) {
              ReactGA.event("Click save appointment", {
                patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
              });

              const { data: saveAppointmentResponse } = await saveAppointment(
                params,
              );

              if (saveAppointmentResponse.status === "error") {
                throw new Error(translations.errorEmailInUse);
              }

              clearStorageAppointment();
              navigate(`/${appointmentId}/success`);
            }
          } catch (error) {
            // @TODO use error alert
            // eslint-disable-next-line no-alert
            alert(getDisplayErrorMessage(error, formatMessage));
          }
        };

        const validationSchema = getValidationSchema(
          "guardianForm",
          validationSchemaTranslations,
          MIN_STRING_INPUT_LENGTH,
          MAX_STRING_INPUT_LENGTH,
        );

        const initialTouched = getInitialTouched(guardianInitialValues);
        const validateOnMount = getValidateOnMount(guardianInitialValues);

        return (
          <Formik
            initialValues={guardianInitialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            initialTouched={initialTouched}
            validateOnMount={validateOnMount}
            enableReinitialize
          >
            {
              children as
                | React.ReactNode
                | ((props: FormikProps<GuardianFormValues>) => ReactNode)
            }
          </Formik>
        );
      }

      const onSubmit = async (values: PatientFormValues) => {
        try {
          if (!appointmentId) {
            throw new Error(translations.defaultError);
          }

          const customerId =
            (selectedCustomer as DatabaseRow<"customers"> | null)?.id ||
            awsSession?.getIdToken()?.payload?.sub;

          const params: AppointmentCreatePaymentParams | SaveAppointmentParams =
            {
              appointmentId,
              customerId,
              creatorType: "patient",
              ...values,
              dateOfBirth: getDateOfBirth(values.dateOfBirth),
              phoneNumber: getPhoneNumber(values.phoneNumber),
              heightFt: +values.heightFt,
              heightIn: +values.heightIn,
              weightLbs: +values.weightLbs,
            };

          if (isPaymentRequired) {
            ReactGA.event("Click confirm and pay", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: appointmentCreatePaymentResponse } =
              await appointmentCreatePayment(params);

            if (appointmentCreatePaymentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            const paymentUrl = getPaymentUrl(
              appointmentCreatePaymentResponse.data,
              appointmentId,
              translations.defaultError,
            );

            clearStorageAppointment();

            navigate(paymentUrl);
          }

          if (!isPaymentRequired) {
            ReactGA.event("Click save appointment", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });
            const { data: saveAppointmentResponse } = await saveAppointment(
              params,
            );

            if (saveAppointmentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            clearStorageAppointment();

            navigate(`/${appointmentId}/success`);
          }
        } catch (error) {
          // @TODO use error alert
          // eslint-disable-next-line no-alert
          alert(getDisplayErrorMessage(error, formatMessage));
        }
      };

      const validationSchema = getValidationSchema(
        "patientForm",
        validationSchemaTranslations,
        MIN_STRING_INPUT_LENGTH,
        MAX_STRING_INPUT_LENGTH,
      );

      const initialTouched = getInitialTouched(patientInitialValues);
      const validateOnMount = getValidateOnMount(patientInitialValues);

      return (
        <Formik
          initialValues={patientInitialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialTouched={initialTouched}
          validateOnMount={validateOnMount}
          enableReinitialize
        >
          {
            children as
              | React.ReactNode
              | ((props: FormikProps<PatientFormValues>) => ReactNode)
          }
        </Formik>
      );
    }
    case "legalGuardian": {
      const onSubmit = async (values: GuardianFormValues) => {
        try {
          if (!appointmentId) {
            throw new Error(translations.defaultError);
          }

          const customerId =
            selectedCustomer?.id || awsSession?.getIdToken()?.payload?.sub;

          const params: AppointmentCreatePaymentParams | SaveAppointmentParams =
            {
              appointmentId,
              customerId: customerId as string | undefined,
              creatorType: "legalGuardian",
              guardianEmail: values.guardianEmail,
              guardianFirstName: values.guardianFirstName,
              guardianLastName: values.guardianLastName,
              guardianPhoneNumber: getPhoneNumber(values.guardianPhoneNumber),
              selectedPatientId: values.selectedPatientId,
              additionalPhoneNumber: values.additionalPhoneNumber
                ? getPhoneNumber(values.additionalPhoneNumber)
                : undefined,
              firstName: values.firstName,
              lastName: values.lastName,
              dateOfBirth: getDateOfBirth(values.dateOfBirth),
              pregnant: values.pregnant,
              wearingDevice: values.wearingDevice,
              heightFt: +values.heightFt,
              heightIn: +values.heightIn,
              weightLbs: +values.weightLbs,
            };

          if (isPaymentRequired) {
            ReactGA.event("Click confirm and pay", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: appointmentCreatePaymentResponse } =
              await appointmentCreatePayment(params);

            if (appointmentCreatePaymentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            const paymentUrl = getPaymentUrl(
              appointmentCreatePaymentResponse.data,
              appointmentId,
              translations.defaultError,
            );

            clearStorageAppointment();
            navigate(paymentUrl);
          }

          if (!isPaymentRequired) {
            ReactGA.event("Click save appointment", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: saveAppointmentResponse } = await saveAppointment(
              params,
            );

            if (saveAppointmentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            clearStorageAppointment();

            navigate(`/${appointmentId}/success`);
          }
        } catch (error) {
          // @TODO use error alert
          // eslint-disable-next-line no-alert
          alert(getDisplayErrorMessage(error, formatMessage));
        }
      };

      const validationSchema = getValidationSchema(
        "guardianForm",
        validationSchemaTranslations,
        MIN_STRING_INPUT_LENGTH,
        MAX_STRING_INPUT_LENGTH,
      );

      const initialTouched = getInitialTouched(guardianInitialValues);
      const validateOnMount = getValidateOnMount(guardianInitialValues);

      return (
        <Formik
          initialValues={guardianInitialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialTouched={initialTouched}
          validateOnMount={validateOnMount}
          enableReinitialize
        >
          {
            children as
              | React.ReactNode
              | ((props: FormikProps<GuardianFormValues>) => ReactNode)
          }
        </Formik>
      );
    }
    case "familyMember": {
      const onSubmit = async (values: GuardianFormValues) => {
        try {
          if (!appointmentId) {
            throw new Error(translations.defaultError);
          }

          const customerId =
            selectedCustomer?.id || awsSession?.getIdToken()?.payload?.sub;

          const params: AppointmentCreatePaymentParams | SaveAppointmentParams =
            {
              appointmentId,
              customerId: customerId as string | undefined,
              creatorType: "familyMember",
              familyMemberEmail: values.guardianEmail,
              familyMemberFirstName: values.guardianFirstName,
              familyMemberLastName: values.guardianLastName,
              familyMemberPhoneNumber: getPhoneNumber(
                values.guardianPhoneNumber,
              ),
              selectedPatientId: values.selectedPatientId,
              additionalPhoneNumber: values.additionalPhoneNumber
                ? getPhoneNumber(values.additionalPhoneNumber)
                : undefined,
              firstName: values.firstName,
              lastName: values.lastName,
              dateOfBirth: getDateOfBirth(values.dateOfBirth),
              pregnant: values.pregnant,
              wearingDevice: values.wearingDevice,
              heightFt: +values.heightFt,
              heightIn: +values.heightIn,
              weightLbs: +values.weightLbs,
            };

          if (isPaymentRequired) {
            ReactGA.event("Click confirm and pay", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: appointmentCreatePaymentResponse } =
              await appointmentCreatePayment(params);

            if (appointmentCreatePaymentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            const paymentUrl = getPaymentUrl(
              appointmentCreatePaymentResponse.data,
              appointmentId,
              translations.defaultError,
            );

            clearStorageAppointment();
            navigate(paymentUrl);
          }

          if (!isPaymentRequired) {
            ReactGA.event("Click save appointment", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: saveAppointmentResponse } = await saveAppointment(
              params,
            );

            if (saveAppointmentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            clearStorageAppointment();
            navigate(`/${appointmentId}/success`);
          }
        } catch (error) {
          // @TODO use error alert
          // eslint-disable-next-line no-alert
          alert(getDisplayErrorMessage(error, formatMessage));
        }
      };

      const validationSchema = getValidationSchema(
        "guardianForm",
        validationSchemaTranslations,
        MIN_STRING_INPUT_LENGTH,
        MAX_STRING_INPUT_LENGTH,
      );

      const initialTouched = getInitialTouched(guardianInitialValues);
      const validateOnMount = getValidateOnMount(guardianInitialValues);

      return (
        <Formik
          initialValues={guardianInitialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialTouched={initialTouched}
          validateOnMount={validateOnMount}
          enableReinitialize
        >
          {
            children as
              | React.ReactNode
              | ((props: FormikProps<GuardianFormValues>) => ReactNode)
          }
        </Formik>
      );
    }
    case "otherPerson": {
      const onSubmit = async (values: OtherPersonFormValues) => {
        try {
          if (!appointmentId) {
            throw new Error(translations.defaultError);
          }

          const customerId =
            selectedCustomer?.id || awsSession?.getIdToken()?.payload?.sub;

          const params: AppointmentCreatePaymentParams | SaveAppointmentParams =
            {
              appointmentId,
              customerId: customerId as string | undefined,
              creatorType: "otherPerson",
              ...values,
              dateOfBirth: getDateOfBirth(values.dateOfBirth),
              phoneNumber: getPhoneNumber(values.phoneNumber),
              heightFt: +values.heightFt,
              heightIn: +values.heightIn,
              weightLbs: +values.weightLbs,
            };

          if (isPaymentRequired) {
            ReactGA.event("Click confirm and pay", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: createdPaymentData } = await appointmentCreatePayment(
              params,
            );

            if (createdPaymentData.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            const paymentUrl = getPaymentUrl(
              createdPaymentData.data,
              appointmentId,
              translations.defaultError,
            );

            clearStorageAppointment();
            navigate(paymentUrl);
          }

          if (!isPaymentRequired) {
            ReactGA.event("Click save appointment", {
              patient_date_of_birth: getDateOfBirth(values.dateOfBirth),
            });

            const { data: saveAppointmentResponse } = await saveAppointment(
              params,
            );

            if (saveAppointmentResponse.status === "error") {
              throw new Error(translations.errorEmailInUse);
            }

            clearStorageAppointment();

            navigate(`/${appointmentId}/success`);
          }
        } catch (error) {
          // @TODO use error alert
          // eslint-disable-next-line no-alert
          alert(getDisplayErrorMessage(error, formatMessage));
        }
      };

      const validationSchema = getValidationSchema(
        "otherPersonForm",
        validationSchemaTranslations,
        MIN_STRING_INPUT_LENGTH,
        MAX_STRING_INPUT_LENGTH,
      );

      return (
        <Formik
          initialValues={otherPersonInitialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {
            children as
              | React.ReactNode
              | ((props: FormikProps<OtherPersonFormValues>) => ReactNode)
          }
        </Formik>
      );
    }
    default: {
      return (
        <Formik initialValues={{}} onSubmit={() => {}}>
          {children}
        </Formik>
      );
    }
  }
};

export default AppointmentCheckoutFormSectionWrapper;
